import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import BaseUISystem from '../BaseUISystem';
import { UIActions } from '../../../State/UI/UIActions';
import {
    AUDIO_UI_BUTTON_CLOSE,
    AUDIO_UI_BUTTON_DEFAULT,
    AUDIO_UI_CAMERA_FLASH,
} from '../../Audio';
import Interactable from '../../../Component/Interactable';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import {
    applyDefaultButtonAnimation,
    applyBounceButtonAnimation,
} from '../UIHelper';
import BaseMenu from './BaseMenu';
import Game from '../../../Game';
import LeaderboardHelper from '../LeaderboardHelper';
import UIImageComponent from '../../../Component/UI/Image';
import GameObject from '../../../GameObject/GameObject';
import Overlay from '../../../Prototype/UI/Overlay';
import Renderable from '../../../Component/Renderable';
import { StartAnimation } from '../../Animation/AnimationHelper';
import { GameActions } from '../../../State/Game/GameActions';
import { ContextType } from '../../../State/Game/IContext';
import { WORLDS } from '../../World';
import FontFaceObserver from 'fontfaceobserver';
import { StartAudio } from '../../Audio/AudioHelper';
import { AudioTrackType } from '../../Audio/AudioClip';
import { AnimationClipEvent } from '../../Animation/AnimationClip';

const BTN_CLOSE = 'close_button';
const BTN_SHARE = 'share_button';
const POSTCARD = 'postcard';
const BACKGROUND = 'postcard/background';
const AVATAR = 'postcard/background/avatar';
const FRAME = 'postcard/background/frame';
const LOGO = 'postcard/background/logo';
const MESSAGE = 'postcard/message';

export default class PostcardMenuHandler extends BaseMenu {
    private overlay: Overlay;
    private background: GameObject;

    constructor(uiSystem: BaseUISystem) {
        super('postcardMenu', uiSystem);
        this.clipShow = 'PostcardBarShow';
    }

    protected onCreate() {
        this.overlay = GameObject.Instantiate(Overlay, {
            color: [255, 255, 255, 255],
            alpha: 1,
            visible: false,
        });
        this.element.addChild(this.overlay);

        this.background = this.element.find(BACKGROUND);

        const btnClose = this.element.find(BTN_CLOSE);
        btnClose.on(GameObjectEvent.SELECTED, () => {
            btnClose.getComponent(Interactable).enabled = false;
            this.system.state.dispatch(UIActions.hidePopup(this.name));
        });
        applyDefaultButtonAnimation(btnClose, AUDIO_UI_BUTTON_CLOSE);

        const shareButton = this.element.find(BTN_SHARE);
        shareButton.on(GameObjectEvent.SELECTED, () => {
            Game.state.dispatch(
                GameActions.setContext(ContextType.SHARE_POSTCARD, {
                    gameObjectId: this.background.id,
                    worldName: this.getWorldName(),
                })
            );
        });
        applyBounceButtonAnimation(shareButton, AUDIO_UI_BUTTON_DEFAULT);
    }

    protected onShow(): void {
        Game.sdk.gameTracking(FBTrackingEvent.Postcard_Share_Show, 1, {
            world: this.getWorldName(),
        });
        StartAnimation(this.element.find(POSTCARD), 'PostcardIntro');
    }

    protected onShown(params: any): void {
        this.showPostcard(params.background, params.place);
    }

    private async showPostcard(backgroundPath: string, place: number) {
        // Load and set avatar.
        LeaderboardHelper.setPlayerAvatar(
            'player_avatar',
            Game.sdk.getAvatar(),
            this.element.find(AVATAR).getComponent(UIImageComponent),
            118
        );

        // Load and set background.
        const texture = `resource/graphics/postcard_backgrounds/${backgroundPath}`;
        Game.resource.add(texture);
        await Game.resource.wait(texture);

        const bgImage = this.background.getComponent(UIImageComponent);
        if (bgImage) {
            bgImage.texture = texture;
        }

        // Load lobster font.
        try {
            await new FontFaceObserver('Lobster').load();
        } catch (e) {
            console.error(e);
        }

        await this.flashEffect();

        await this.writeEffect(
            Game.locale
                .getText(`PostcardMessage${place}`)
                .replace(
                    '[X]',
                    Game.locale.getText(
                        WORLDS[Game.player.getActiveWorldNumber() - 1]
                    )
                )
        );

        this.element.find(BTN_SHARE).getComponent(Renderable).visible = Game.sdk.driver.hasShareableMomentsSupport();
    }

    private async flashEffect(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.overlay.getComponent(Renderable).visible = true;
            this.element.find(AVATAR).getComponent(Renderable).visible = true;
            this.element.find(FRAME).getComponent(Renderable).visible = true;
            this.element.find(LOGO).getComponent(Renderable).visible = true;

            StartAudio(
                this.system.scene,
                AUDIO_UI_CAMERA_FLASH,
                AudioTrackType.SFX
            );

            StartAnimation(this.overlay, 'Alpha', {
                duration: 300,
                startAlpha: 1,
                endAlpha: 0,
            });

            StartAnimation(
                this.background.getComponent(UIImageComponent).gameObject,
                'Alpha',
                {
                    duration: 300,
                    startAlpha: 0,
                    endAlpha: 1,
                }
            ).once(AnimationClipEvent.COMPLETE, () => resolve());
        });
    }

    private async writeEffect(str: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const message = this.element.find(MESSAGE);
            message.getComponent(Renderable).visible = true;
            StartAnimation(message, 'Writing', {
                message: str,
                duration: 300,
            }).once(AnimationClipEvent.COMPLETE, () => resolve());
        });
    }

    private getWorldName(): string {
        return WORLDS[Game.player.getActiveWorldNumber() - 1];
    }
}
